import { useEffect, useState } from 'react';
import {
  Banknote,
  Receipt,
  Filter,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ShoppingCart,
  CreditCard,
  UserCheck,
  ArrowUpRight,
  Package,
  Coins,
  WalletCards,
  Landmark,
  TrendingUp,
  ArrowDownLeft,
  Plus,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import CashTransactionModal from '../components/CashTransactionModal';

function formatCurrency(val) {
  if (val === null || val === undefined || isNaN(Number(val))) return 'Rs 0';
  const num = Number(val);
  const formatted = num.toLocaleString('en-PK', {
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `Rs ${formatted}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getPresetDates(preset) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  switch (preset) {
    case 'Today': {
      const t = fmt(now);
      return { from: t, to: t };
    }
    case 'Yesterday': {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const t = fmt(y);
      return { from: t, to: t };
    }
    case 'This Week': {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const start = new Date(now);
      start.setDate(diff);
      return { from: fmt(start), to: fmt(now) };
    }
    case 'This Month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: fmt(start), to: fmt(end) };
    }
    case 'Last Month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: fmt(start), to: fmt(end) };
    }
    case 'This Year': {
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(now.getFullYear(), 11, 31);
      return { from: fmt(start), to: fmt(end) };
    }
    case 'All Time':
      return { from: '', to: '' };
    default:
      return null;
  }
}

export default function Accounting() {
  const [method, setMethod] = useState('all');
  // The Dashboard is a today view, so Accounts opens on the same period.
  // Users can still select any other period from the filter.
  const [datePreset, setDatePreset] = useState('Today');
  const [dateFrom, setDateFrom] = useState(() => getPresetDates('Today').from);
  const [dateTo, setDateTo] = useState(() => getPresetDates('Today').to);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCashModalOpen, setIsCashModalOpen] = useState(false);
  const [cashModalType, setCashModalType] = useState('deposit');

  const openCashModal = (t = 'deposit') => {
    setCashModalType(t);
    setIsCashModalOpen(true);
  };

  const loadAccounting = async (targetPage = page) => {
    setLoading(true);
    try {
      let url = `/accounting?page=${targetPage}&limit=${limit}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      if (method && method !== 'all') url += `&method=${method}`;

      const res = await api.get(url);
      setData(res);
    } catch (err) {
      toast.error(err.message || 'Failed to load accounting');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounting(page);
  }, [page]);

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    const range = getPresetDates(preset);
    if (range) {
      setDateFrom(range.from);
      setDateTo(range.to);
    }
  };

  const handleCustomDateChange = (type, val) => {
    setDatePreset('Custom');
    if (type === 'from') setDateFrom(val);
    if (type === 'to') setDateTo(val);
  };

  const handleFilterSubmit = (e) => {
    e?.preventDefault();
    setPage(1);
    loadAccounting(1);
  };

  const handleReset = () => {
    setMethod('all');
    setDatePreset('All Time');
    const range = getPresetDates('All Time');
    setDateFrom(range.from);
    setDateTo(range.to);
    setPage(1);
    setTimeout(() => {
      loadAccounting(1);
    }, 0);
  };

  const totals = data?.totals || {};
  const entries = data?.entries || [];
  const pagination = data?.pagination || { total: 0, page: 1, limit: 20, totalPages: 1 };
  const showingFilteredDigital = Boolean(data?.dateFrom || data?.dateTo);

  const startEntry = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endEntry = Math.min(pagination.total, pagination.page * pagination.limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Accounts</h1>
          <p className="mt-1 text-sm text-slate-500">
            Method-wise ledger with incoming, outgoing, and running balance history.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openCashModal('deposit')}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-emerald-700 transition active:scale-95"
          >
            <ArrowDownLeft className="h-4 w-4" />
            <span>Deposit Cash</span>
          </button>
          <button
            type="button"
            onClick={() => openCashModal('withdraw')}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-rose-600 px-4 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-rose-700 transition active:scale-95"
          >
            <ArrowUpRight className="h-4 w-4" />
            <span>Withdraw Funds</span>
          </button>
        </div>
      </div>

      {/* Stat Cards - Row 1 (Core Accounts & Balances) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* PROFIT Card */}
        <div className={clsx(
          "rounded-2xl border p-5 shadow-xs transition hover:shadow-sm",
          (totals.profit?.netProfit ?? 0) >= 0
            ? "border-emerald-200 bg-emerald-50/40"
            : "border-rose-200 bg-rose-50/40"
        )}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                PROFIT {datePreset ? `(${datePreset.toUpperCase()})` : ''}
              </p>
              <h3 className={clsx(
                "mt-2 text-2xl font-extrabold",
                (totals.profit?.netProfit ?? 0) >= 0 ? "text-emerald-700" : "text-rose-700"
              )}>
                {formatCurrency(totals.profit?.netProfit ?? 0)}
              </h3>
            </div>
            <div className={clsx(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border",
              (totals.profit?.netProfit ?? 0) >= 0
                ? "border-emerald-200/80 bg-emerald-100/60 text-emerald-600"
                : "border-rose-200/80 bg-rose-100/60 text-rose-600"
            )}>
              <TrendingUp className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-500 font-medium">
            <span className={clsx(
              "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold",
              (totals.profit?.netProfit ?? 0) >= 0
                ? "bg-emerald-100 text-emerald-800"
                : "bg-rose-100 text-rose-800"
            )}>
              {totals.profit?.margin ?? 0}% margin
            </span>
            <span>Gross: {formatCurrency(totals.profit?.grossProfit ?? 0)}</span>
          </div>
        </div>

        {/* CUSTOMER ADVANCE Card */}
        <div className="rounded-2xl border border-amber-100 bg-amber-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">CUSTOMER ADVANCE</p>
              <h3 className="mt-2 text-2xl font-extrabold text-amber-700">
                {formatCurrency(totals.customerAdvance)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-200/80 bg-amber-100/50 text-amber-600">
              <WalletCards className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">
            {totals.customerAdvanceCount || 0} customer{Number(totals.customerAdvanceCount) === 1 ? '' : 's'} with advance
          </p>
        </div>

        {/* SUPPLIER ADVANCE Card */}
        <div className="rounded-2xl border border-violet-100 bg-violet-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">SUPPLIER ADVANCE</p>
              <h3 className="mt-2 text-2xl font-extrabold text-violet-700">
                {formatCurrency(totals.supplierAdvance)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-violet-200/80 bg-violet-100/50 text-violet-600">
              <Coins className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">
            {totals.supplierAdvanceCount || 0} supplier{Number(totals.supplierAdvanceCount) === 1 ? '' : 's'} with advance
          </p>
        </div>

        {/* EXPENSES Card */}
        <div className="rounded-2xl border border-rose-100 bg-rose-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">EXPENSES</p>
              <h3 className="mt-2 text-2xl font-extrabold text-rose-600">
                {formatCurrency(totals.expenses)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-rose-200/80 bg-rose-100/50 text-rose-500">
              <Receipt className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Expense entries total</p>
        </div>

        {/* AVAILABLE CASH / BALANCE Card */}
        <div className="rounded-2xl border border-blue-100 bg-blue-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                {method === 'cash' || method === 'all'
                  ? 'AVAILABLE CASH'
                  : method === 'deposit'
                  ? 'TOTAL DEPOSITS'
                  : method === 'withdraw'
                  ? 'TOTAL WITHDRAWALS'
                  : method === 'credit'
                  ? 'NET UDHAAR (RECEIVABLE)'
                  : method === 'bank'
                  ? 'BANK BALANCE'
                  : 'AVAILABLE BALANCE'}
              </p>
              <h3 className="mt-2 text-2xl font-extrabold text-blue-700">
                {formatCurrency(totals.availableCash)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-blue-200/80 bg-blue-100/50 text-blue-600">
              <Banknote className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">
            {method === 'cash' || method === 'all'
              ? 'All-time cash balance (synchronized with Dashboard)'
              : method === 'deposit'
              ? 'Total cash deposited in selected period'
              : method === 'withdraw'
              ? 'Total cash withdrawn in selected period'
              : method === 'credit'
              ? 'Customer receivables minus supplier payables'
              : 'Closing balance for the selected ledger'}
          </p>
        </div>

        {/* STOCK VALUE Card */}
        <div className="rounded-2xl border border-cyan-100 bg-cyan-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">STOCK VALUE</p>
              <h3 className="mt-2 text-2xl font-extrabold text-cyan-700">
                {formatCurrency(totals.stockValue)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-cyan-200/80 bg-cyan-100/50 text-cyan-600">
              <Package className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Current inventory value at cost price</p>
        </div>

        {/* PAYABLE Card */}
        <div className="rounded-2xl border border-rose-100 bg-rose-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">PAYABLE</p>
              <h3 className="mt-2 text-2xl font-extrabold text-rose-700">
                {formatCurrency(totals.payable)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-rose-200/80 bg-rose-100/50 text-rose-600">
              <Landmark className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Outstanding supplier dues</p>
        </div>
        {/* CASH SALES Card */}
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">CASH SALES</p>
              <h3 className="mt-2 text-2xl font-extrabold text-emerald-700">
                {formatCurrency(totals.cashSales)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-emerald-200/80 bg-emerald-100/50 text-emerald-600">
              <ShoppingCart className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Direct cash sales in period</p>
        </div>

        {/* CARD / DIGITAL BALANCE Card */}
        <div className="rounded-2xl border border-indigo-100 bg-indigo-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                {showingFilteredDigital ? 'CARD / DIGITAL NET' : 'CARD / DIGITAL BALANCE'}
              </p>
              <h3 className="mt-2 text-2xl font-extrabold text-indigo-700">
                {formatCurrency(totals.cardSales)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-indigo-200/80 bg-indigo-100/50 text-indigo-600">
              <CreditCard className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">
            {showingFilteredDigital ? 'Digital inflows less outflows in selected period' : 'Current card, online & bank balance'}
          </p>
        </div>

        {/* CARD / ONLINE / BANK SALES Card */}
        <div className="rounded-2xl border border-sky-100 bg-sky-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">CARD / DIGITAL SALES</p>
              <h3 className="mt-2 text-2xl font-extrabold text-sky-700">
                {formatCurrency(totals.digitalSales)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-sky-200/80 bg-sky-100/50 text-sky-600">
              <CreditCard className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Card, online & bank sales in selected period</p>
        </div>

        {/* UDHAAR GIVEN Card */}
        <div className="rounded-2xl border border-amber-100 bg-amber-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">UDHAAR GIVEN</p>
              <h3 className="mt-2 text-2xl font-extrabold text-amber-700">
                {formatCurrency(totals.udhaarGiven)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-200/80 bg-amber-100/50 text-amber-600">
              <ArrowUpRight className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Credit sales extended to customers</p>
        </div>

        {/* STAFF PAYMENTS Card */}
        <div className="rounded-2xl border border-purple-100 bg-purple-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">STAFF PAYMENTS</p>
              <h3 className="mt-2 text-2xl font-extrabold text-purple-700">
                {formatCurrency(totals.staffPayments)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-purple-200/80 bg-purple-100/50 text-purple-600">
              <UserCheck className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Salary & advance payouts in period</p>
        </div>

        {/* CASH DEPOSITS Card */}
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">CASH DEPOSITS</p>
              <h3 className="mt-2 text-2xl font-extrabold text-emerald-700">
                {formatCurrency(totals.cashDeposits)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-emerald-200/80 bg-emerald-100/50 text-emerald-600">
              <ArrowDownLeft className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Cash deposited in period</p>
        </div>

        {/* CASH WITHDRAWALS Card */}
        <div className="rounded-2xl border border-rose-100 bg-rose-50/25 p-5 shadow-xs transition hover:shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">CASH WITHDRAWALS</p>
              <h3 className="mt-2 text-2xl font-extrabold text-rose-700">
                {formatCurrency(totals.cashWithdrawals)}
              </h3>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-rose-200/80 bg-rose-100/50 text-rose-600">
              <ArrowUpRight className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400 font-medium">Cash withdrawn in period</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-xs">
        <form onSubmit={handleFilterSubmit} className="flex flex-wrap items-end gap-3 sm:gap-4">
          {/* Method Filter */}
          <div className="min-w-[140px] flex-1 sm:flex-none">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              METHOD / TYPE
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="all">All</option>
              <option value="cash">Cash</option>
              <option value="deposit">Deposit</option>
              <option value="withdraw">Withdrawal</option>
              <option value="credit">Credit (Udhaar)</option>
              <option value="bank">Bank</option>
              <option value="card">Card</option>
              <option value="online">Online</option>
            </select>
          </div>

          {/* Date Preset Filter */}
          <div className="min-w-[140px] flex-1 sm:flex-none">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              DATE FILTER
            </label>
            <select
              value={datePreset}
              onChange={(e) => handleDatePresetChange(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="Last Month">Last Month</option>
              <option value="This Year">This Year</option>
              <option value="All Time">All Time</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          {/* From Date */}
          <div className="min-w-[150px] flex-1 sm:flex-none">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              FROM
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => handleCustomDateChange('from', e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* To Date */}
          <div className="min-w-[150px] flex-1 sm:flex-none">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              TO
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => handleCustomDateChange('to', e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-1 sm:pt-0">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-xs transition hover:bg-indigo-700 disabled:opacity-60"
            >
              <Filter className="h-4 w-4" />
              Filter
            </button>
            <button
              type="button"
              onClick={handleReset}
              title="Reset Filters"
              className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-xs transition hover:bg-slate-50 hover:text-slate-900"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>

      {/* Ledger Transactions Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#112240] text-white">
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-100">
                  DATE
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-100">
                  DETAILS
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-100">
                  DEBIT
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-100">
                  CREDIT
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-100 text-right">
                  BALANCE
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center text-sm text-slate-400">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="h-5 w-5 animate-spin text-indigo-600" />
                      <span>Loading ledger entries...</span>
                    </div>
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center text-sm text-slate-400">
                    No transactions found for the selected filter.
                  </td>
                </tr>
              ) : (
                entries.map((row, idx) => (
                  <tr key={`${row.type}-${row.id}-${idx}`} className="hover:bg-slate-50/70 transition-colors">
                    {/* DATE */}
                    <td className="px-6 py-4 text-sm font-medium text-slate-600 whitespace-nowrap align-top">
                      {formatDate(row.transaction_date || row.transaction_time)}
                    </td>

                    {/* DETAILS */}
                    <td className="px-6 py-4 align-top">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900 font-mono tracking-tight">
                            {row.reference}
                          </span>
                          {row.type === 'cash_deposit' && (
                            <span className="inline-flex items-center rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                              Deposit
                            </span>
                          )}
                          {row.type === 'cash_withdraw' && (
                            <span className="inline-flex items-center rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-800">
                              Withdrawal
                            </span>
                          )}
                          {row.type === 'credit_sale' && (
                            <span className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                              Udhaar (Credit Sale)
                            </span>
                          )}
                          {row.type === 'credit_purchase' && (
                            <span className="inline-flex items-center rounded-md bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-800">
                              Payable (Credit Purchase)
                            </span>
                          )}
                        </div>
                        <div className={clsx(
                          "text-xs font-semibold",
                          row.type === 'cash_withdraw' || row.type === 'credit_purchase' || Number(row.credit) > 0
                            ? "text-rose-600"
                            : "text-emerald-600"
                        )}>
                          {row.description}
                        </div>
                        <div>
                          <span className={clsx(
                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold border capitalize",
                            row.payment_method === 'credit'
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : row.payment_method === 'bank'
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-slate-100 text-slate-700 border-slate-200"
                          )}>
                            {row.payment_method || 'Cash'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* DEBIT */}
                    <td className="px-6 py-4 text-sm font-semibold text-emerald-600 whitespace-nowrap align-top">
                      {Number(row.debit) > 0 ? formatCurrency(row.debit) : (
                        <span className="text-slate-300 font-normal">-</span>
                      )}
                    </td>

                    {/* CREDIT */}
                    <td className="px-6 py-4 text-sm font-semibold text-rose-600 whitespace-nowrap align-top">
                      {Number(row.credit) > 0 ? formatCurrency(row.credit) : (
                        <span className="text-slate-300 font-normal">-</span>
                      )}
                    </td>

                    {/* BALANCE */}
                    <td className="px-6 py-4 text-sm font-bold text-emerald-700 whitespace-nowrap text-right align-top">
                      {formatCurrency(row.balance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer / Pagination */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-6 py-4 bg-white border-t border-slate-100 text-sm text-slate-500">
          <div>
            Showing {startEntry} to {endEntry} of {pagination.total} entries
          </div>

          <div className="flex items-center gap-1.5 self-center sm:self-auto">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1 || loading}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {Array.from({ length: pagination.totalPages || 1 }, (_, i) => i + 1)
              .filter((p) => {
                const cur = pagination.page;
                return p === 1 || p === pagination.totalPages || (p >= cur - 1 && p <= cur + 1);
              })
              .map((p, idx, arr) => (
                <span key={p} className="flex items-center">
                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                    <span className="px-1 text-slate-400">...</span>
                  )}
                  <button
                    onClick={() => setPage(p)}
                    className={clsx(
                      'inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg px-2 text-xs font-semibold transition',
                      p === pagination.page
                        ? 'border border-indigo-600 bg-indigo-50/50 text-indigo-600'
                        : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    {p}
                  </button>
                </span>
              ))}

            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <CashTransactionModal
        isOpen={isCashModalOpen}
        onClose={() => setIsCashModalOpen(false)}
        onSuccess={() => loadAccounting(page)}
        initialType={cashModalType}
        currentRemainingCash={totals.availableCash ?? 0}
        accountBalances={totals.accountBalances ?? {}}
      />
    </div>
  );
}
